/** Shared utilities for {@link StudentRow}, {@link StudentCard} and {@link StudentsDataTable}. */

import { Clock, Mail, Phone, type LucideIcon } from 'lucide-react';
import type { ManagedByValue } from '@/components/common/ManagedByMultiSelect';
import type { StudentRecord, StudentSituation } from '@/types/remote/student.types';
import {
  isReactivatableUserStatus,
  type FolderSituation,
  type OnboardingStatus,
  type WeeklyAvailabilitySlot,
} from '@/types/user.types';
import { getRoleLabel } from '@/utils/role-label-utils';
import {
  type StudentRemoteFilters,
  type StudentRemoteTabView,
  type StudentStatusConfig,
  STUDENT_REMOTE_TAB_USER_STATUS,
  STUDENT_ACTIVE_STATUS_CONFIG,
  STUDENT_PENDING_STATUS_CONFIG,
  STUDENT_PROGRAM_CONFIG,
  STUDENT_SITUATION_CONFIG,
} from '@/components/students/student-configs';
import { getStudentCategory, getStudentSituation } from '@/utils/common-utils';
import { resolveUserContacts } from '@/utils/user-contact-utils';

export {
  completionBarColor,
  getCompletionStatus,
  getStudentCategory,
  getStudentGenderLabel,
  getStudentMilestoneStatus,
  getStudentRemoteStatusFilterKeys,
  getStudentSituation,
} from '@/utils/common-utils';

/** RBAC actions available on a student row/card. Single source of truth. */
export interface StudentRowPageActions {
  canEditRow?: boolean;
  canViewRow?: boolean;
  canViewSessions?: boolean;
  canViewReports?: boolean;
  canArchiveRow?: boolean;
  canReactivateRow?: boolean;
}

/** Snapshot passed to {@link ArchiveStudentModal} from the list row. */
export interface StudentArchiveTarget {
  userId: string;
  studentName: string;
  isTutoringInProgress: boolean;
}

/** Extended actions for {@link StudentsDataTable} (adds {@link DataTable}-level permissions). */
export interface StudentDataTablePageActions extends StudentRowPageActions {
  canExportCsv?: boolean;
}

/** Status + situation badge configs derived from a student record. */
export interface StudentDerivedStatus {
  statusConfig: StudentStatusConfig | undefined;
  StatusIcon: LucideIcon;
  situationConfig: StudentStatusConfig;
  SituationIcon: LucideIcon;
}

/** Primary contact icon + value, with optional secondary shown under it. */
export interface StudentPrimaryContact {
  icon: LucideIcon;
  value: string;
  secondaryValue: string | null;
}

/** One pair row from the student's `user.binomes` list. */
type StudentPairPartner = NonNullable<NonNullable<StudentRecord['user']>['binomes']>[number];

/** List row id, or a user id already passed in, for the full-state GET. */
export function resolveStudentExpandedUserId(
  expandedRowId: string | null | undefined,
  rows: readonly StudentRecord[],
): string | undefined {
  if (!expandedRowId) return undefined;
  const foundById = rows.find((row) => row.id === expandedRowId);
  if (foundById) return foundById.userId;
  const foundByUserId = rows.find((row) => row.userId === expandedRowId);
  return foundByUserId?.userId ?? expandedRowId;
}

/** First + last name, or a generic fallback when the list row has no user. */
export function formatStudentDisplayName(record: StudentRecord | undefined): string {
  const name = `${record?.user?.firstName ?? ''} ${record?.user?.lastName ?? ''}`.trim();
  return name || 'Cet élève';
}

/** True when the situation badge is an active tutoring engagement. */
export function isStudentTutoringInProgress(record: StudentRecord | undefined): boolean {
  if (!record) return false;
  return (
    getStudentSituation({
      onboardingStatus: record.onboardingStatus,
      studentSituation: record.studentSituation,
      _tutorCount: record.user?.binomes?.length,
    }) === 'tutoring_in_progress'
  );
}

/** Build the archive modal target from the list row (name + situation). */
export function buildStudentArchiveTarget(
  record: StudentRecord | undefined,
  userId: string,
): StudentArchiveTarget {
  return {
    userId,
    studentName: formatStudentDisplayName(record),
    isTutoringInProgress: isStudentTutoringInProgress(record),
  };
}

/** Hide archive when the row is already archived. */
export function canShowArchiveRow(
  status: string | undefined,
  canArchiveRow: boolean | undefined,
): boolean {
  return !!canArchiveRow && status !== 'archived';
}

/** Hide reactivate on pre-registered or non-reactivatable statuses. */
export function canShowReactivateRow(
  status: string | undefined,
  tab: string,
  canReactivateRow: boolean | undefined,
): boolean {
  return !!canReactivateRow && tab !== 'pre_registered' && isReactivatableUserStatus(status);
}

/** Derives badge configs for status + situation from a student record. */
export function deriveStudentStatusConfigs(record: StudentRecord): StudentDerivedStatus {
  const { category, status } = getStudentCategory({
    onboardingStatus: record.onboardingStatus,
    folderSituation: record.folderSituation,
    studentSituation: record.studentSituation,
    user: record.user,
    linkedUserId: record.linkedUserId,
  });

  const statusConfig =
    category === 'pre_registered'
      ? STUDENT_PENDING_STATUS_CONFIG[status as keyof typeof STUDENT_PENDING_STATUS_CONFIG]
      : STUDENT_ACTIVE_STATUS_CONFIG[status as keyof typeof STUDENT_ACTIVE_STATUS_CONFIG];

  const situationKey = getStudentSituation({
    onboardingStatus: record.onboardingStatus,
    studentSituation: record.studentSituation,
  });
  const situationConfig = STUDENT_SITUATION_CONFIG[situationKey];

  return {
    statusConfig,
    StatusIcon: statusConfig?.icon ?? Clock,
    situationConfig,
    SituationIcon: situationConfig.icon,
  };
}

/** Returns true when the student email is an internal placeholder (minor). */
export function isInternalStudentEmail(email?: string | null): boolean {
  return !!email?.endsWith('@students.internal.ednov');
}

/** Phone vs email; minors use the linked parent's contact. Empty when neither exists. */
export function deriveStudentContact(record: StudentRecord): StudentPrimaryContact {
  const isMinor = isInternalStudentEmail(record.user?.email);
  const source = isMinor ? record.linkedUser : record.user;
  const resolved = resolveUserContacts(source?.primaryContactKind, source?.email, source?.phone);
  return {
    icon: resolved.kind === 'phone' ? Phone : Mail,
    value: resolved.primary?.value ?? '',
    secondaryValue: resolved.secondary?.value ?? null,
  };
}

/** Formats a date as dd/mm/yyyy, or "—". */
export function formatStudentDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Normalises a {@link ManagedByValue} filter to the string[] format expected by the API.
 * Returns undefined when no meaningful value is set.
 */
export function buildManagedByParam(
  managedByIds: ManagedByValue | undefined,
): string[] | undefined {
  if (managedByIds === undefined) return undefined;
  if (managedByIds === 'all') return ['all'];
  if (managedByIds === 'unmanaged') return ['unmanaged'];
  if (Array.isArray(managedByIds) && managedByIds.length > 0) return managedByIds;
  return undefined;
}

/** Map UI filters + tab to remote-student list params. */
export function buildStudentRemoteListFilters(
  filters: StudentRemoteFilters,
  activeTab: StudentRemoteTabView,
) {
  return {
    onboardingStatus: filters.onboardingStatus as OnboardingStatus | undefined,
    folderSituation: filters.folderSituation as FolderSituation | undefined,
    studentSituation: filters.studentSituation as StudentSituation | undefined,
    managedByIds: buildManagedByParam(filters.managedByIds),
    linkedUserId: filters.parentId,
    search: filters.search,
    programId: filters.programId,
    subject: filters.subject,
    levelId: filters.levelId,
    partnerId: filters.partnerId,
    establishmentId: filters.establishmentId,
    stepKeys: filters.stepKeys,
    participationStudies: filters.participationStudies,
    gender: filters.gender,
    departmentCodes: filters.departmentCodes,
    userStatus: STUDENT_REMOTE_TAB_USER_STATUS[activeTab],
  };
}

/** True when any remote-student chip / search filter is set. */
export function hasStudentRemoteActiveFilters(filters: StudentRemoteFilters): boolean {
  return (
    !!filters.onboardingStatus ||
    !!filters.folderSituation ||
    !!filters.studentSituation ||
    !!filters.managedByIds ||
    !!filters.parentId ||
    !!filters.search ||
    !!filters.programId ||
    !!filters.subject ||
    !!filters.levelId ||
    !!filters.partnerId ||
    !!filters.establishmentId ||
    (filters.stepKeys?.length ?? 0) > 0 ||
    filters.participationStudies !== undefined ||
    (filters.departmentCodes?.length ?? 0) > 0 ||
    !!filters.userStatus
  );
}

/** Formats a date as dd/mm/yy, or "—". */
export function formatDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

/** Days elapsed since `dateStr`, or "—". */
export function daysSince(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
  return `${days} jour${days === 1 ? '' : 's'}`;
}

/** Open a document URL in a new tab via a blob preview. */
export async function openDocumentPreview(url: string): Promise<void> {
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const pdfBlob = new Blob([blob], { type: blob.type || 'application/pdf' });
    const objectUrl = URL.createObjectURL(pdfBlob);
    const w = window.open(objectUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    if (!w) window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/** Download a URL as a named file via blob. */
export async function downloadViaBlob(url: string, filename: string): Promise<void> {
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, '_blank');
  }
}

/** Capitalize the first letter, or "—". */
export function capitalizeFirst(str?: string): string {
  if (!str) return '—';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/** Student contact, or linked-parent contact for internal emails. */
export function getEffectiveContact(record: StudentRecord): { email?: string; phone?: string } {
  if (!isInternalStudentEmail(record.user?.email)) {
    return { email: record.user?.email, phone: record.user?.phone };
  }
  return { email: record.linkedUser?.email, phone: record.linkedUser?.phone ?? undefined };
}

/** Registration manager first + last name, or "—". */
export function formatRegistrationManagerName(record: StudentRecord): string {
  if (!record.registrationManager) return '—';
  return `${record.registrationManager.firstName} ${record.registrationManager.lastName}`;
}

/** Unique tutors linked via the student's pairs. */
export function getAccompanyingTutors(record: StudentRecord): Array<{
  binomeId: string;
  tutorUserId: string;
  name: string;
  program?: string | null;
  managedByName: string;
}> {
  const binomes = (record.user?.binomes ?? []) as StudentPairPartner[];
  const seenTutorIds = new Set<string>();
  const tutors: Array<{
    binomeId: string;
    tutorUserId: string;
    name: string;
    program?: string | null;
    managedByName: string;
  }> = [];

  for (const b of binomes) {
    if (b.role && b.role !== 'student') continue;
    const tutorUserId = b.partnerUserId ?? b.id;
    if (!tutorUserId || seenTutorIds.has(tutorUserId)) continue;
    seenTutorIds.add(tutorUserId);
    tutors.push({
      binomeId: b.id,
      tutorUserId,
      name:
        (typeof b.partnerName === 'string' ? b.partnerName.trim() : '') || getRoleLabel('tutor'),
      program: b.program ?? null,
      managedByName: b.managedBy?.fullName?.trim() || '—',
    });
  }
  return tutors;
}

/** Program name/abbreviation, or a short id fallback. */
export function getProgramLabel(programId: string, programsList: any[]): string {
  const program = programsList.find((p: any) => p.id === programId);
  return program?.name ?? program?.abbreviation ?? programId.slice(0, 8);
}

/** Header program chips: catalog config first, then API name fields. */
export function mapStudentDetailHeaderPrograms(
  programsRaw: unknown,
  programsList: any[],
): Array<{
  id: string;
  label: string;
  cfg: (typeof STUDENT_PROGRAM_CONFIG)[string] | undefined;
}> {
  if (!Array.isArray(programsRaw) || programsRaw.length === 0) return [];
  return programsRaw.map((p: any, i: number) => {
    const programId = typeof p === 'string' ? p : (p?.programId ?? p?.id ?? String(i));
    const cfg = STUDENT_PROGRAM_CONFIG[programId];
    const label =
      cfg?.label ??
      p?.programName ??
      p?.name ??
      p?.abbreviation ??
      (typeof p === 'string' ? getProgramLabel(p, programsList) : '?');
    return { id: String(p?.programId ?? p?.id ?? i), label, cfg };
  });
}

/** Accept a slot array or `{ weekly: slots }`. */
function unwrapWeeklyAvailability(availability: unknown): unknown {
  if (Array.isArray(availability)) return availability;
  if (availability && typeof availability === 'object' && 'weekly' in availability) {
    return (availability as { weekly?: unknown }).weekly;
  }
  return [];
}

/** Normalize day/start/end aliases onto {@link WeeklyAvailabilitySlot}. */
export function normalizeDetailWeeklyAvailabilitySlots(
  availability: unknown,
): WeeklyAvailabilitySlot[] {
  const raw = unwrapWeeklyAvailability(availability);
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s: any) => ({
      day: s?.day ?? s?.dayOfTheWeek,
      start: s?.start ?? s?.startTime,
      end: s?.end ?? s?.endTime,
    }))
    .filter((s) => s?.day != null && s?.start && s?.end);
}

/** Parent autocomplete options: `first last – email`. */
export function mapParentsRemoteSearchOptions(
  records:
    | Array<{
        userId: string;
        user?: { firstName?: string | null; lastName?: string | null; email?: string | null };
      }>
    | undefined,
): Array<{ value: string; label: string }> {
  return (
    records?.map((record) => {
      const parentUser = record.user;
      const firstName = parentUser?.firstName || '';
      const lastName = parentUser?.lastName || '';
      const email = parentUser?.email || '';
      const displayName = [firstName, lastName].filter(Boolean).join(' ');
      const label = displayName + (email ? ` – ${email}` : '');
      return {
        value: record.userId,
        label: label || email || record.userId,
      };
    }) ?? []
  );
}
